import React, { useEffect, useState } from 'react';
import { getLogs, ApiLog } from '@api/logs';

interface LogFilterState {
    message: string;
    user: string;
    minLevel: string;
    fromDate: string;
    toDate: string;
}

const levelOptions = [
    { value: '', label: 'All' },
    { value: '0', label: 'Trace' },
    { value: '1', label: 'Debug' },
    { value: '2', label: 'Information' },
    { value: '3', label: 'Warning' },
    { value: '4', label: 'Error' },
    { value: '5', label: 'Critical' },
];

export default function LogsAdmin() {
    const [logs, setLogs] = useState<ApiLog[]>([]);
    const [filter, setFilter] = useState<LogFilterState>({
        message: '',
        user: '',
        minLevel: '',
        fromDate: '',
        toDate: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(200);

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadLogs() {
        setLoading(true);
        setError(null);
        try {
            const data = await getLogs({
                filtermessage: filter.message || undefined,
                filteruser: filter.user || undefined,
                minlevel: filter.minLevel !== '' ? Number(filter.minLevel) : undefined,
                fromdate: filter.fromDate || undefined,
                todate: filter.toDate || undefined,
                skip: pageIndex * pageSize,
                take: pageSize,
            });
            setLogs(data ?? []);
        } catch (e: any) {
            setError(e.message || 'Error loading logs');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }

    function handleFilterChange(
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) {
        const { name, value } = e.target;
        setFilter((prev) => ({ ...prev, [name]: value }));
    }

    function handleFilterKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            setPageIndex(0);
            loadLogs();
        }
    }

    // Helpers
    function formatTimestamp(value: string | undefined) {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString();
    }

    function mapLevel(level: number): string {
        switch (level) {
            case 0:
                return 'Trace';
            case 1:
                return 'Debug';
            case 2:
                return 'Info';
            case 3:
                return 'Warn';
            case 4:
                return 'Error';
            case 5:
                return 'Critical';
            default:
                return String(level);
        }
    }

    return (
        <div className="adm-shell adm-shell--col">
            {/* Filters bovenin */}
            <div className="adm-logs-filters">
                <div style={{ paddingRight: 10, minWidth: 180, maxWidth: 260 }}>
                    <div className="adm-filter-label">Message</div>
                    <input
                        name="message"
                        value={filter.message}
                        onChange={handleFilterChange}
                        onKeyDown={handleFilterKeyDown}
                        className="adm-input"
                        placeholder="contains..."
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 160, maxWidth: 220 }}>
                    <div className="adm-filter-label">User</div>
                    <input
                        name="user"
                        value={filter.user}
                        onChange={handleFilterChange}
                        onKeyDown={handleFilterKeyDown}
                        className="adm-input"
                        placeholder="user name"
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 140, maxWidth: 180 }}>
                    <div className="adm-filter-label">Min level</div>
                    <select
                        name="minLevel"
                        value={filter.minLevel}
                        onChange={handleFilterChange}
                        className="adm-input"
                    >
                        {levelOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ paddingRight: 10, minWidth: 150, maxWidth: 180 }}>
                    <div className="adm-filter-label">From</div>
                    <input
                        type="date"
                        name="fromDate"
                        value={filter.fromDate}
                        onChange={handleFilterChange}
                        className="adm-input"
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 150, maxWidth: 180 }}>
                    <div className="adm-filter-label">To</div>
                    <input
                        type="date"
                        name="toDate"
                        value={filter.toDate}
                        onChange={handleFilterChange}
                        className="adm-input"
                    />
                </div>

                {/* Page size + navigatie */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div className="adm-filter-label">Page size</div>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value) || 50);
                            setPageIndex(0);
                        }}
                        className="adm-input"
                        style={{ width: 80 }}
                    >
                        {[20, 50, 100, 200].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        marginLeft: 'auto',
                        alignItems: 'flex-end',
                    }}
                >
                    <button
                        type="button"
                        className="adm-btn adm-btn-primary"
                        onClick={() => {
                            setPageIndex(0);
                            loadLogs();
                        }}
                        disabled={loading}
                    >
                        Search
                    </button>
                    <button
                        type="button"
                        className="adm-btn adm-btn-secondary"
                        onClick={() => {
                            setFilter({
                                message: '',
                                user: '',
                                minLevel: '',
                                fromDate: '',
                                toDate: '',
                            });
                            setLogs([]);
                            setPageIndex(0);
                        }}
                        disabled={loading}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {error && (
                <div className="adm-status adm-status--error">{error}</div>
            )}

            {/* Scrollbare grid */}
            <div className="adm-logs-grid">
                <table className="adm-table adm-table--compact">
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Level</th>
                            <th>User</th>
                            <th style={{ width: '60%' }}>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => {
                            const isSelected = log.id === selectedLogId;

                            return (
                                <tr
                                    key={log.id}
                                    className={`adm-row${isSelected ? ' is-selected' : ''}`}
                                    onClick={() =>
                                        setSelectedLogId((prev) =>
                                            prev === log.id ? null : log.id,
                                        )
                                    }
                                >
                                    {/* timestamp from logDate */}
                                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        {formatTimestamp(log.timestamp)}
                                    </td>

                                    {/* logLevel */}
                                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        {log.logLevelName ?? mapLevel(log.logLevel)}
                                    </td>

                                    {/* userName */}
                                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        {log.user ?? ''}
                                    </td>

                                    {/* message, met wordwrap */}
                                    <td
                                        style={{
                                            verticalAlign: 'top',
                                            // standaard: 1 regel, ellipsis
                                            // geselecteerd: volledige tekst met word-wrap
                                            whiteSpace: isSelected ? 'pre-wrap' : 'nowrap',
                                            overflow: isSelected ? 'visible' : 'hidden',
                                            textOverflow: isSelected ? 'clip' : 'ellipsis',
                                        }}
                                    >
                                        {log.message}
                                    </td>
                                </tr>
                            );
                        })}
                        {logs.length === 0 && !loading && (
                            <tr>
                                <td colSpan={5} className="adm-empty">No logs</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paging controls */}
            <div className="adm-logs-foot">
                <div>
                    Page {pageIndex + 1} · Showing {logs.length} item(s)
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        className="adm-btn adm-btn-secondary"
                        onClick={() => {
                            if (pageIndex > 0) {
                                const newIndex = pageIndex - 1;
                                setPageIndex(newIndex);
                                // na setState is direct loadLogs() prima, omdat loadLogs de actuele state leest
                                loadLogs();
                            }
                        }}
                        disabled={loading || pageIndex === 0}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="adm-btn adm-btn-secondary"
                        onClick={() => {
                            if (logs.length === pageSize) {
                                const newIndex = pageIndex + 1;
                                setPageIndex(newIndex);
                                loadLogs();
                            }
                        }}
                        disabled={loading || logs.length < pageSize}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}